/**
 * Player session management for bulletproof reconnection.
 *
 * Uses a stable playerId (UUID) stored in localStorage to identify players
 * across reconnects, page refreshes, and browser restarts.
 */

const PLAYER_ID_KEY = "quiz_player_id";
const SESSION_KEY = "quiz_session";

/**
 * Get or create a stable player ID.
 * This ID persists across all sessions and is used to identify the player.
 */
export function getOrCreatePlayerId() {
    let playerId = localStorage.getItem(PLAYER_ID_KEY);
    if (!playerId) {
        // Generate a UUID v4
        playerId = crypto.randomUUID();
        localStorage.setItem(PLAYER_ID_KEY, playerId);
    }
    return playerId;
}

/**
 * Get the current player ID (without creating one).
 */
export function getPlayerId() {
    return localStorage.getItem(PLAYER_ID_KEY);
}

/**
 * Check if there's an active session to rejoin.
 */
export function hasActiveSession() {
    const session = getSession();
    return session !== null && session.roomCode && session.playerId;
}

/**
 * Get the current session info.
 * @returns {{ roomCode: string, nickname: string, playerId: string } | null}
 */
export function getSession() {
    try {
        const data = localStorage.getItem(SESSION_KEY);
        if (!data) return null;
        const session = JSON.parse(data);
        // Validate session has required fields
        if (session && session.roomCode && session.nickname && session.playerId) {
            return session;
        }
        return null;
    } catch (e) {
        console.warn("[Session] Failed to parse session:", e);
        return null;
    }
}

/**
 * Save session info for reconnection.
 */
export function saveSession(roomCode, nickname, playerId) {
    const session = { roomCode, nickname, playerId };
    localStorage.setItem(SESSION_KEY, JSON.stringify(session));
    console.log("[Session] Saved:", session);
}

/**
 * Clear the current session (e.g., when leaving a quiz).
 */
export function clearSession() {
    localStorage.removeItem(SESSION_KEY);
    console.log("[Session] Cleared");
}

// Legacy exports for backwards compatibility during transition
// These can be removed once all code is migrated

export function reconnectModeIsAvailable() {
    return hasActiveSession();
}

export function getReconnectRoom() {
    const session = getSession();
    return session?.roomCode || null;
}

export function getReconnectPlayer() {
    const session = getSession();
    return session?.nickname || null;
}

export function enableReconnectMode(roomCode, nickname) {
    const playerId = getOrCreatePlayerId();
    saveSession(roomCode, nickname, playerId);
}

export function disableReconnectMode() {
    clearSession();
}
